/**
 * Utility functions for safely handling localStorage operations
 */

/**
 * Safely get and parse a value from localStorage
 * @param {string} key - The key to retrieve from localStorage
 * @param {any} defaultValue - Default value to return if key doesn't exist or parsing fails
 * @returns {any} - The parsed value or defaultValue
 */
export const getFromLocalStorage = (key, defaultValue = null) => {
  try {
    const value = localStorage.getItem(key);
    if (!value) return defaultValue;
    
    try {
      // Try to parse as JSON
      return JSON.parse(value);
    } catch (e) {
      // If parsing fails, return the string value
      return value;
    }
  } catch (error) {
    console.error(`Error getting ${key} from localStorage:`, error);
    return defaultValue;
  }
};

/**
 * Safely set a value in localStorage
 * @param {string} key - The key to set in localStorage
 * @param {any} value - The value to store
 * @returns {boolean} - Whether the operation was successful
 */
export const setInLocalStorage = (key, value) => {
  try {
    // If value is an object or array, stringify it
    const valueToStore = typeof value === 'object' ? JSON.stringify(value) : value;
    localStorage.setItem(key, valueToStore);
    return true;
  } catch (error) {
    console.error(`Error setting ${key} in localStorage:`, error);
    return false;
  }
};

/**
 * Safely remove a value from localStorage
 * @param {string} key - The key to remove from localStorage
 * @returns {boolean} - Whether the operation was successful
 */
export const removeFromLocalStorage = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing ${key} from localStorage:`, error);
    return false;
  }
};

/**
 * Safely clear all values from localStorage
 * @returns {boolean} - Whether the operation was successful
 */
export const clearLocalStorage = () => {
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    return false;
  }
}; 